import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import '../theme/app-theme.css';
import { canvasBackground } from '../theme/canvasBackground';

export const APPEARANCE_KEY = "uzdpart360:appearance";
const AppearanceContext = createContext({ mode: "dark", isDark: true, toggle: () => {} });
const readMode = () => {
  try { return localStorage.getItem(APPEARANCE_KEY) === "light" ? "light" : "dark"; }
  catch { return "dark"; }
};
// Popups render outside every themed screen: the static ones read the mode
// here, notification CSS reads html[data-appearance].
let currentMode = readMode();
const modeListeners = new Set();
export const getAppearanceMode = () => currentMode;
export const subscribeAppearance = listener => { modeListeners.add(listener); return () => modeListeners.delete(listener); };
// A global preference, not a global CSS/Ant Design override. Each page opts in.
export function AppearanceProvider({ children }) {
  const [mode, setMode] = useState(readMode);
  const transitionTimer = useRef(null);
  useLayoutEffect(() => {
    document.documentElement.style.backgroundColor = canvasBackground(mode === 'dark');
    document.documentElement.style.colorScheme = mode;
    document.documentElement.dataset.appearance = mode;
  }, [mode]);
  const changeMode = useCallback(next => {
    if (next === mode) return;
    clearTimeout(transitionTimer.current);
    if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      document.documentElement.classList.add('app-theme-transition');
      transitionTimer.current = setTimeout(() => {
        document.documentElement.classList.remove('app-theme-transition');
        transitionTimer.current = null;
      }, 320);
    } else document.documentElement.classList.remove('app-theme-transition');
    setMode(next);
  }, [mode]);
  const toggle = useCallback(() => changeMode(mode === "dark" ? "light" : "dark"), [changeMode, mode]);
  useEffect(() => () => {
    clearTimeout(transitionTimer.current);
    document.documentElement.classList.remove('app-theme-transition');
  }, []);
  useEffect(() => { try { localStorage.setItem(APPEARANCE_KEY, mode); } catch {} }, [mode]);
  useEffect(() => {
    currentMode = mode;
    document.documentElement.dataset.appearance = mode;
    modeListeners.forEach(listener => listener());
  }, [mode]);
  useEffect(() => {
    const sync = event => { if (event.key === APPEARANCE_KEY) changeMode(event.newValue === "light" ? "light" : "dark"); };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [changeMode]);
  const value = useMemo(() => ({ mode, isDark: mode === "dark", toggle }), [mode, toggle]);
  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}
export const useAppearance = () => useContext(AppearanceContext);
